import { useQueryStates } from "nuqs";
import type { Options } from "nuqs";

import { dispatchParsers, teleopParsers } from "./parsers";

/**
 * Hook to access and modify dispatch-related URL query parameters using nuqs.
 * This provides type-safe access to URL state that persists across page refreshes.
 *
 * @param options - Optional nuqs configuration options (e.g., history, shallow, throttleMs)
 * @returns A tuple of [queryStates, setQueryStates] where:
 *   - queryStates: An object containing the current values of all dispatch query parameters
 *   - setQueryStates: A function to update one or more query parameters
 *
 * @example
 * ```tsx
 * // Read and write dispatch URL parameters
 * const [queryStates, setQueryStates] = useDispatchQueryStates();
 *
 * // Read a parameter from the URL
 * if (queryStates.selectedMarker) {
 *   console.log('Selected marker:', queryStates.selectedMarker);
 * }
 *
 * // Update URL parameters (this updates the browser URL)
 * setQueryStates({
 *   selectedMarker: 'marker-123'
 * });
 *
 * // Clear a parameter by setting to null
 * setQueryStates({ selectedMarker: null });
 *
 * // With options
 * const [queryStates, setQueryStates] = useDispatchQueryStates({
 *   history: 'push',
 *   shallow: false
 * });
 * ```
 */
export const useDispatchQueryStates = (options?: Options) => {
  return useQueryStates(dispatchParsers, options);
};

/**
 * Hook to access and modify teleop-related URL query parameters using nuqs.
 * This provides type-safe access to URL state that persists across page refreshes.
 *
 * @param options - Optional nuqs configuration options (e.g., history, shallow, throttleMs)
 * @returns A tuple of [queryStates, setQueryStates] where:
 *   - queryStates: An object containing the current values of all teleop query parameters
 *   - setQueryStates: A function to update one or more query parameters
 *
 * @example
 * ```tsx
 * // Read and write teleop URL parameters
 * const [queryStates, setQueryStates] = useTeleopQueryStates();
 *
 * // Read a parameter from the URL
 * if (queryStates.selectedMarker) {
 *   console.log('Selected marker:', queryStates.selectedMarker);
 * }
 *
 * // Update URL parameters (this updates the browser URL)
 * setQueryStates({
 *   selectedMarker: 'marker-123'
 * });
 *
 * // Clear a parameter by setting to null
 * setQueryStates({ selectedMarker: null });
 *
 * // With options
 * const [queryStates, setQueryStates] = useTeleopQueryStates({
 *   history: 'push',
 *   shallow: false
 * });
 * ```
 */
export const useTeleopQueryStates = (options?: Options) => {
  return useQueryStates(teleopParsers, options);
};
